import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Check, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import Turnstile from './Turnstile';

export default function FeedbackWidget({ liftForTabBar = false }: { liftForTabBar?: boolean } = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const close = () => {
    if (sending) return;
    setOpen(false);
    // Reset after exit transition
    setTimeout(() => { setMessage(''); setSent(false); setError(''); setTurnstileToken(''); }, 200);
  };

  // Hide entirely on the messages route — the floating button conflicts with the chat send control.
  if (location.pathname.startsWith('/dashboard/messages')) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = message.trim();
    if (!trimmed) { setError(t('feedback.empty')); return; }
    setSending(true);
    try {
      await api.submitFeedback({
        message: trimmed,
        page_url: typeof window !== 'undefined' ? window.location.href : location.pathname,
        turnstile_token: turnstileToken || undefined,
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.message || t('feedback.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher — offset on mobile so it doesn't collide with Exchange filter pill.
          z-[1000] keeps it above Leaflet's internal layers which use z-index up to 700. */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{ isolation: 'isolate' }}
        className={`fixed right-4 sm:right-6 z-[1000] flex items-center gap-2
                   bg-stone-950 hover:bg-stone-800 text-white text-xs font-medium
                   px-4 py-2.5 rounded-full shadow-lg transition-colors pointer-events-auto ${
                     liftForTabBar
                       ? 'bottom-[calc(3.5rem+env(safe-area-inset-bottom)+1rem)] md:bottom-6'
                       : 'bottom-4 sm:bottom-6'
                   }`}
        aria-label={t('feedback.open')}
      >
        <MessageCircle size={14} />
        <span className="hidden sm:inline">{t('feedback.open')}</span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-4"
             style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}
             onClick={close}
             onMouseDown={(e) => e.stopPropagation()}
             onPointerDown={(e) => e.stopPropagation()}
             onTouchStart={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-[fadeSlideUp_0.2s_ease-out]"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <h2 className="font-display text-lg text-stone-950">{t('feedback.title')}</h2>
              <button onClick={close} className="p-1.5 rounded-lg hover:bg-stone-100" aria-label={t('feedback.cancel')}>
                <X size={16} className="text-stone-500" />
              </button>
            </div>

            {sent ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <Check size={22} className="text-green-600" />
                </div>
                <p className="text-sm text-stone-800 font-medium">{t('feedback.sent')}</p>
                <p className="text-xs text-stone-500 mt-1">{t('feedback.sentDesc')}</p>
                <button onClick={close}
                  className="mt-6 text-xs text-stone-500 hover:text-stone-900 transition-colors">
                  {t('feedback.close')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5">
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={t('feedback.placeholder')}
                  rows={5}
                  maxLength={5000}
                  autoFocus
                  className="w-full text-sm p-3 border border-stone-200 rounded-lg
                             focus:outline-none focus:border-stone-500 resize-none
                             placeholder:text-stone-400"
                />
                <Turnstile onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
                {error && (
                  <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg mt-3">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button type="button" onClick={close}
                    className="px-4 py-2 text-xs text-stone-500 hover:text-stone-900 transition-colors">
                    {t('feedback.cancel')}
                  </button>
                  <button type="submit" disabled={sending}
                    className="flex items-center gap-1.5 bg-stone-950 hover:bg-stone-800 disabled:opacity-60
                               text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">
                    <Send size={12} />
                    {sending ? t('feedback.sending') : t('feedback.submit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
